/**
 * useInviteHandler
 *
 * URL query 의 invite 파라미터를 감지하여 다음 흐름을 처리:
 *   1. `?inviteGroupId=...&modelId=...` 가 있고 로그인 상태면:
 *      - POST /qbase/api/joinShareGroupByInvite → 공유그룹 자동 참여 (이미 멤버면 skip)
 *      - modelId 가 있으면 EventBus.emit('OPEN-MODEL-BY-ID', { modelId }) 발행
 *      - 사용자에게 결과 메시지 표시
 *      - URL query 정리 (history.replaceState)
 *   2. 로그인 안 된 상태면 sessionStorage 에 보관 → 로그인 후 useEffect 재실행 시 처리
 *
 * App.js 또는 인증 후 화면 마운트 시 한 번만 호출.
 */
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useSelector } from 'react-redux';
import BaseService from 'services/BaseService';
import EventBus from 'utils/hooks/EventBus';
import ConfirmDialog from 'components/shared/ConfirmDialog';

const REACT_APP_BE_API = process.env.REACT_APP_BE_API || '/qcore/home';
const PENDING_INVITE_KEY = 'pendingInvite';

// ============================================================
// Module-level immediate capture
//   React 마운트/Router redirect 이전에 URL query 를 즉시 sessionStorage 에 보관.
//   이 파일이 import 되는 순간 (App.js 의 import 처리 시점) 실행됨.
//   이 시점에는 아직 URL 의 ?inviteGroupId=...&modelId=... 가 유지된 상태.
// ============================================================
if (typeof window !== 'undefined') {
    try {
        const url = new URL(window.location.href);
        const inviteGroupId = url.searchParams.get('inviteGroupId');
        if (inviteGroupId) {
            const modelId = url.searchParams.get('modelId');
            const shrgrpAsmtNm = url.searchParams.get('shrgrpAsmtNm');
            sessionStorage.setItem(PENDING_INVITE_KEY,
                JSON.stringify({ inviteGroupId, modelId, shrgrpAsmtNm }));
        }
    } catch (e) {
        console.warn('[Invite] (module-load) 캡처 실패:', e);
    }
}

/**
 * ConfirmDialog 동적 마운트 (모델 자동 열기 전 사용자 확인)
 * 사용자가 '확인' 클릭 → true, '취소' / ESC / 닫기 → false
 */
function confirmOpenModel({ shrgrpNm, modelNm, modelId }) {
    return new Promise((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);
        const cleanup = (result) => {
            setTimeout(() => {
                try { root.unmount(); } catch { /* ignore */ }
                if (container.parentNode) container.parentNode.removeChild(container);
            }, 200);
            resolve(result);
        };
        root.render(
            <ConfirmDialog
                isOpen={true}
                type="info"
                title="초대된 모델 열기"
                confirmText="확인 (모델 열기)"
                cancelText="취소"
                confirmButtonColor="blue-600"
                onConfirm={() => cleanup(true)}
                onCancel={() => cleanup(false)}
                onClose={() => cleanup(false)}
                onRequestClose={() => cleanup(false)}
            >
                <div className="text-sm space-y-2">
                    <p>이메일 초대를 통해 다음 모델 열기 요청을 받았습니다.</p>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 space-y-1">
                        {shrgrpNm && (
                            <div>
                                <span className="font-semibold text-gray-600 dark:text-gray-300">공유그룹: </span>
                                <span className="text-gray-900 dark:text-gray-100">{shrgrpNm}</span>
                            </div>
                        )}
                        <div>
                            <span className="font-semibold text-gray-600 dark:text-gray-300">모델: </span>
                            <span className="text-gray-900 dark:text-gray-100">
                                {modelNm || modelId}
                                {modelNm && modelId && (
                                    <span className="text-xs text-gray-500 ml-2">({modelId})</span>
                                )}
                            </span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        '취소' 시 자동 열기를 중단합니다. 이후 메뉴에서 직접 모델을 선택할 수 있습니다.
                    </p>
                </div>
            </ConfirmDialog>
        );
    });
}

/** URL 또는 sessionStorage 에서 invite 파라미터 추출 */
function readInviteParams() {
    const url = new URL(window.location.href);
    const inviteGroupId = url.searchParams.get('inviteGroupId');
    const modelId = url.searchParams.get('modelId');
    const shrgrpAsmtNm = url.searchParams.get('shrgrpAsmtNm');
    if (inviteGroupId) return { inviteGroupId, modelId, shrgrpAsmtNm, fromUrl: true };

    // 로그인 전 → sessionStorage 에 보관된 값
    try {
        const raw = sessionStorage.getItem(PENDING_INVITE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.inviteGroupId) return { ...parsed, fromUrl: false };
        }
    } catch { /* ignore */ }
    return null;
}

/** URL query 제거 (정리) */
function cleanUrl() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('inviteGroupId');
        url.searchParams.delete('modelId');
        url.searchParams.delete('shrgrpAsmtNm');
        window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
}

/**
 * 로그인 전 invite URL 접근 시 보관용 — SignIn 화면 마운트 시 호출.
 * URL 에 invite 가 있으면 sessionStorage 에 저장 (로그인 후 활용).
 */
export function captureInviteToSession() {
    const url = new URL(window.location.href);
    const inviteGroupId = url.searchParams.get('inviteGroupId');
    if (!inviteGroupId) return;
    const modelId = url.searchParams.get('modelId');
    try {
        sessionStorage.setItem(PENDING_INVITE_KEY,
            JSON.stringify({ inviteGroupId, modelId }));
    } catch (e) { console.warn('[Invite] sessionStorage 저장 실패:', e); }
}

/**
 * 메인 hook — 로그인 상태가 되면 자동 처리.
 * Redux auth.user.signedIn 등으로 로그인 감지 가능. 여기서는 단순히
 * sessionToken 또는 user 객체 존재 여부로 판단.
 */
export function useInviteHandler() {
    const signedIn = useSelector(state => state.auth?.session?.signedIn);
    const userId = useSelector(state => state.auth?.user?.userId || state.auth?.user?.usrid);

    useEffect(() => {
        if (!signedIn) {
            return;
        }

        const invite = readInviteParams();

        if (!invite) {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                // 1. 공유그룹 자동 참여 (모델 메타정보 함께 받기)
                const joinResp = await BaseService.post(
                    `${REACT_APP_BE_API}/qbase/api/joinShareGroupByInvite`,
                    {
                        mdlrmdlShrgrpId: invite.inviteGroupId,
                        mdlrmdlId: invite.modelId || ''  // 백엔드가 modelNm 조회용으로 사용
                    },
                    { withCredentials: true, baseURL: '' }
                );

                if (cancelled) return;
                const joined = !!joinResp.data?.joined;
                const alreadyMember = !!joinResp.data?.alreadyMember;
                const shrgrpNm = joinResp.data?.shrgrpNm || '';
                const modelNm = joinResp.data?.modelNm || '';
                // 과제명: URL 쿼리가 우선, 없으면 백엔드가 모델 공유 row 에서 derive 한 값 사용
                // (옛 메일 링크 호환 — 옛 링크엔 shrgrpAsmtNm 가 없음)
                const resolvedAsmtNm = invite.shrgrpAsmtNm || joinResp.data?.shrgrpAsmtNm || '';

                // SHOW-MSG 핸들러는 다이어그램 화면 진입 후 등록됨 — emit 시점에 미등록일 수 있으므로
                // EventBus.emit 은 호출하되 핸들러 없으면 무시됨 (안전).
                if (joined) {
                    EventBus.emit('SHOW-MSG', '공유그룹에 자동 참여되었습니다.');
                } else if (alreadyMember) {
                } else {
                    console.warn('[Invite] 공유그룹 참여 응답:', joinResp.data);
                    EventBus.emit('SHOW-MSG', '공유그룹 참여에 실패했습니다.');
                }

                // 2. 모델 열기
                //   - 신규 가입(joined=true): 의도가 명확하므로 확인 다이얼로그 생략하고 즉시 열기 (G1 해소)
                //   - 이미 멤버(alreadyMember): 재방문일 수 있으므로 확인 다이얼로그 유지
                if (invite.modelId) {
                    let userConfirmed = true;
                    if (alreadyMember) {
                        userConfirmed = await confirmOpenModel({
                            shrgrpNm,
                            modelNm,
                            modelId: invite.modelId,
                        });
                    } else {
                    }

                    if (cancelled) return;

                    if (!userConfirmed) {
                        // 취소 시 sessionStorage 정리 (MenuModel 픽업 방지)
                        try { sessionStorage.removeItem('pendingModelOpen'); } catch { /* */ }
                    } else {
                        // 두 경로로 처리 (MenuModel 핸들러 마운트 타이밍 무관)
                        //    A) EventBus.emit: MenuModel 이 이미 마운트된 경우 즉시 처리
                        //    B) sessionStorage 'pendingModelOpen': MenuModel 마운트 시 픽업
                        try {
                            sessionStorage.setItem('pendingModelOpen', JSON.stringify({
                                modelId: invite.modelId,
                                inviteGroupId: invite.inviteGroupId,
                                shrgrpAsmtNm: resolvedAsmtNm,
                                shrgrpNm,
                                timestamp: Date.now(),
                            }));
                        } catch (e) { console.warn('[Invite] pendingModelOpen 저장 실패:', e); }

                        EventBus.emit('OPEN-MODEL-BY-ID', {
                            modelId: invite.modelId,
                            inviteGroupId: invite.inviteGroupId,
                            shrgrpAsmtNm: resolvedAsmtNm,
                            shrgrpNm,
                            userId,
                            source: 'invite'
                        });
                    }
                }
            } catch (err) {
                // HTTP 410 GONE: 백엔드가 공유취소·종료된 초대를 거절한 정상 경로
                if (err?.response?.status === 410) {
                    const backendMsg = err?.response?.data?.message
                        || '이 초대는 더 이상 유효하지 않습니다.';
                    console.warn('[Invite] 만료된 초대:', backendMsg);
                    EventBus.emit('SHOW-MSG', backendMsg);
                    // 모델 자동 열기 시도 중단 (sessionStorage 정리)
                    try { sessionStorage.removeItem('pendingModelOpen'); } catch { /* */ }
                } else {
                    console.error('[Invite] 처리 실패:', err);
                    const msg = '초대 처리 중 오류: ' + (err?.response?.data?.message || err.message || '알 수 없음');
                    console.warn('[Invite]', msg);
                    EventBus.emit('SHOW-MSG', msg);
                }
            } finally {
                // 3. 정리
                try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch { /* */ }
                cleanUrl();
            }
        })();

        return () => { cancelled = true; };
    }, [signedIn, userId]);
}
